import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output } from '@angular/core';
import * as Models from '../dynamic-grid-by-data/dynamic-grid.interface';
import {BsDatepickerConfig} from 'ngx-bootstrap/datepicker';

export interface DateRangePickerOutput {
    dateRange: Array<string>;
    columnName: string;
    sortOrder?: string;
    sortOrderName?: string;
}

@Component({
    selector: 'ui-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.less']
})
export class DateRangePickerComponent implements OnInit, OnChanges {

    @Input() start: Date;
    @Input() end: Date;
    @Input() hasSort = false;
    @Input() sortOrderName: string;
    @Input() sortOrderCurrent: string;
    @Input() columnName: string;
    @Input() isFiltered = false;
    @Input() positionDateRange: string = 'left';
    @Input() showInput: boolean = false;
    @Input() bsConfig: Partial<BsDatepickerConfig> = {
        containerClass: '',
        showWeekNumbers: false,
        selectFromOtherMonth: true,
        customTodayClass: 'bs-today-custom'
    };

    @Input() hideCursor: boolean;
    @Input()
    set displayDateRange(value: boolean) {
        if (typeof value === 'boolean') {
            this.toggle();
        }
    }
    @Input() isSettingDefaultDate: boolean = false;

    @Output() hide = new EventEmitter<boolean>();
    @Output() onCanceled = new EventEmitter<boolean>();

    @Output() dateRangeChanged: EventEmitter<DateRangePickerOutput> =
        new EventEmitter<DateRangePickerOutput>();

    constructor(
        private elementRef: ElementRef
    ) {}

    isDateRangePickerVisible = false;
    sortOrder = '';
    sortOrders: typeof Models.SortOrder = Models.SortOrder;
    error: string = null;
    private datepickerTheme = '';
    outsideEvent: Event;

    lStart;
    lEnd;

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event): void {
        const isNotOutsideClick = (!this.outsideEvent || !(this.outsideEvent.target === event.target));
        if (isNotOutsideClick && !this.isDateRangePickerVisible) {
            this.setDateToInitialDate();
        }

        if (!this.elementRef.nativeElement.contains(event.target) && this.isDateRangePickerVisible && isNotOutsideClick) {
            this.toggle();
            this.outsideEvent = null;
        }
    }

    ngOnInit(): void {
        this.lStart = this.start;
        this.lEnd   = this.end;
    }

    ngOnChanges(): void {
        if (this.start) {
            this.start = new Date(this.start);
        }

        if (this.end) {
            this.end = new Date(this.end);
        }
    }

    private isValidDateRange(): boolean {
        return this.start <= this.end || !this.start && !this.end;
    }

    private onSelect(): void {
        this.error = null;
        this.setTime();

        const startDate = this.start ? (new Date(this.start)).toUTCString() : null;
        const endDate = this.end ? (new Date(this.end)).toUTCString() : null;

        if (!this.isValidDateRange()) {
            this.error = 'Date range is invalid';
            return;
        }

        const dateData: string[] = startDate && endDate ? [ startDate, endDate ] : [];

        this.dateRangeChanged.emit({
            dateRange: dateData,
            columnName: this.columnName,
        });
        this.lStart = this.start;
        this.lEnd = this.end;
        this.toggle();
    }

    toggle(event?: Event): void {
        this.setDateToInitialDate();
        this.isDateRangePickerVisible = !this.isDateRangePickerVisible;
        this.outsideEvent = event;
        this.hide.emit(this.isDateRangePickerVisible);
    }

    openDatePicker(): void {
        this.toggle();
        if (this.isSettingDefaultDate) {
            this.setDefaultDate();
        }
    }

    setDefaultDate(): void {
        this.start = this.start === null ? new Date() : this.start;
        this.end = this.end === null ? new Date() : this.end;
    }

    setDateToInitialDate(): void {
        this.start = this.lStart;
        this.end = this.lEnd;
    }

    sort(sortOrder: string): void {
        this.sortOrder = sortOrder;
        this.sortOrderCurrent = sortOrder;
    }

    isAscForLittleIcon(): boolean {
        return (this.sortOrderCurrent
            && this.sortOrderCurrent.includes(this.sortOrderName)
            && this.sortOrderCurrent.includes(` ${Models.SortOrder.ASC.toLowerCase()}`));
    }

    isAsc(): boolean {
        return (this.isAscForLittleIcon()
            || this.sortOrder === ` ${Models.SortOrder.ASC.toLowerCase()}`);
    }

    isDescForLittleIcon(): boolean {
        return (this.sortOrderCurrent
            && this.sortOrderCurrent.includes(this.sortOrderName)
            && this.sortOrderCurrent.includes(` ${Models.SortOrder.DESC.toLowerCase()}`));
    }

    isDesc(): boolean {
        return (this.isDescForLittleIcon()
            || this.sortOrder === ` ${Models.SortOrder.DESC.toLowerCase()}`);
    }

    apply(event: MouseEvent): void {
        event.stopPropagation();
        this.onSelect();
    }

    cancel(event: MouseEvent): void {
        event.stopPropagation();
        this.toggle();

        this.start = this.lStart;
        this.end = this.lEnd;

        /**
         * bugfix 2371751
         */
        if (!this.start || !this.end || this.error) {
            this.start = null;
            this.end = null;
            this.error = null;
        }
        this.onCanceled.emit();
    }

    clear(event: MouseEvent): void {
        event.stopPropagation();
        this.start = null;
        this.end = null;
        this.sortOrder = this.sortOrderName = '';
        this.onSelect();
    }

    private setTime(): void {
        if (this.start) {
            this.start.setHours(0, 0, 0, 0);
        }

        if (this.end) {
            this.end.setHours(23, 59, 59, 0);
        }
    }

    updateError(): void {
        if (this.end >= this.start) {
            this.error = null;
        }
    }

    isApplyDisabled(): boolean {
        return !this.start || !this.end || !!this.error;
    }
}

